import { Component, Inject, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Complaint, ComplaintPosition, complaintPriorities, ComplaintsDocumentType, ComplaintsDocumentTypeDict, ComplaintsDocumentTypes, DocumentStatus, PersonsService } from '@icc/b2b/shared';
import { DIALOG_DATA, DialogRef } from '@icc/ui';
import { ComplaintService } from '../complaint.service';
import { Common, parseDate } from '@icc/shared/helpers';
import { NgxPermissionsService } from 'ngx-permissions';
import { ConfigService, core } from '@icc/shared';
import { Subscription } from 'rxjs';

@Component({
    selector: 'icc-complaint-edit-dialog',
    templateUrl: './complaint-edit-dialog.component.html',
    styleUrls: ['./complaint-edit-dialog.component.scss'],
})
export class ComplaintEditDialogComponent implements OnInit, OnChanges, OnDestroy {
    statuses: DocumentStatus[] = [];
    status: DocumentStatus | undefined;

    assignedToOptions: Record<string, string> = {};
    workersList;
    assignedToFilteredOptions: Record<string, string> = {};
    complaintType: ComplaintsDocumentType = ComplaintsDocumentType.Complaint;
    documentComplaintType: ComplaintsDocumentType = ComplaintsDocumentType.Complaint;
    documentAssignedTo: string | Complaint['assignedTo'] = 'authorizedRetailer';
    documentResponsibleUser: number | null = null;
    documentAssignedUser: number | null = null;
    documentPriority: string | null = null;
    documentPurchaseDate: Date | null = null;
    documentStatus: number | null = null;
    documentName: string | null = null;
    documentComplaintOrderNumber: string | null = null;

    hasAccessTo = {
        manageComplaint: false,
        editStatus: false,
        editAssign: false,
        editComplaintOrderNumber: false,
    };

    documentTypes = ComplaintsDocumentTypes;
    documentTypesDict = ComplaintsDocumentTypeDict;
    number = Number;

    documentPriorityGroup: string;
    documentPriorityValue: number;
    documentPriorities = complaintPriorities;

    private subscriptions: Subscription[] = [];

    authorizedRetailerDistributorComplaint = false;
    distributorManufacturerComplaint = false;

    constructor(
        private dialogRef: DialogRef,
        @Inject(DIALOG_DATA)
        public data: {
            complaint: Complaint,
            appointments: any[],
            positionsWorkflowsGroups: Map<number, ComplaintPosition[]>,
            positions: ComplaintPosition[],
        },
        private complaintService: ComplaintService,
        private personsService: PersonsService,
        private permissionsService: NgxPermissionsService,
        private configService: ConfigService,
    ) {
        this.authorizedRetailerDistributorComplaint = this.data.complaint.clientName.length < 1 && this.data.complaint.authorizedRetailerCompanyId !== null;
        this.distributorManufacturerComplaint = this.data.complaint.clientName.length < 1 && this.data.complaint.authorizedRetailerCompanyId === null;
        this.subscriptions.push(
            this.permissionsService.permissions$.subscribe(() => {
                this.checkAccess();
            }),
        );
    }

    async ngOnInit() {
        this.complaintService.loadContractors();
        await this.complaintService.contractorsLoaded;
        this.assignedToOptions = this.complaintService.getAssignToOptions(this.data.complaint);
        this.assignedToFilteredOptions = this.complaintService.getFilteredAssignToOptions(this.assignedToOptions, this.data.positionsWorkflowsGroups, this.data.complaint);
        this.documentComplaintType = this.data.complaint.documentTypeId;
        this.documentAssignedTo = this.getAssignToValue();
        this.documentResponsibleUser = this.data.complaint[this.getUserType()]?.userId ?? null;
        this.documentAssignedUser = this.data.complaint[this.getUserType() + 'UserId' as 'authorizedRetailerUserId' | 'distributorUserId' | 'manufacturerUserId'] ?? null;
        this.documentPriority = this.complaintService.getPriority(this.data.complaint) ?? null;
        this.documentPriorityGroup = this.documentPriority ? this.documentPriority.slice(0, 1) : '';
        this.documentPriorityValue = this.documentPriority ? +this.documentPriority.slice(1) : 0;
        this.documentPurchaseDate = this.data.complaint.purchaseDate ? new Date(this.data.complaint.purchaseDate) : null;
        this.documentName = this.data.complaint.documentName ?? null;
        this.documentComplaintOrderNumber = this.data.complaint.complaintOrderNumber ?? null;
        await this.personsService.loaded;
        this.workersList = this.personsService.workersList;
        await this.complaintService.workflowsLoaded;
        this.statuses = this.complaintService.getAvailableStatuses(this.data.complaint, this.data.positions, this.data.positionsWorkflowsGroups, this.data.appointments, this.getUserType());
        this.status = this.complaintService.statuses.find((s) => {
            const userType = this.getUserType();
            if (userType === 'authorizedRetailer') {
                return s.id === +this.data.complaint.authorizedRetailerStatusId;
            } else if (userType === 'distributor') {
                return s.id === +this.data.complaint.distributorStatusId;
            }
            return s.id === +this.data.complaint.manufacturerStatusId;
        });
        this.documentStatus = this.status?.id ?? null;
    }

    async ngOnChanges() {
        this.complaintService.loadContractors();
        await this.complaintService.contractorsLoaded;
        this.assignedToOptions = this.complaintService.getAssignToOptions(this.data.complaint);
        this.assignedToFilteredOptions = this.complaintService.getFilteredAssignToOptions(this.assignedToOptions, this.data.positionsWorkflowsGroups, this.data.complaint);
        await this.personsService.loaded;
        this.workersList = this.personsService.workersList;
        this.statuses = this.complaintService.getAvailableStatuses(this.data.complaint, this.data.positions, this.data.positionsWorkflowsGroups, this.data.appointments, this.getUserType());
        this.status = this.complaintService.statuses.find((s) => {
            const userType = this.getUserType();
            if (userType === 'authorizedRetailer') {
                return s.id === +this.data.complaint.authorizedRetailerStatusId;
            } else if (userType === 'distributor') {
                return s.id === +this.data.complaint.distributorStatusId;
            }
            return s.id === +this.data.complaint.manufacturerStatusId;
        });
        this.checkAccess();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    async checkAccess() {
        this.hasAccessTo.manageComplaint = await this.permissionsService.hasPermission('complaints-manage') && ((this.data.complaint.assignedTo && this.data.complaint.assignedTo.startsWith(this.getUserType())) || await this.permissionsService.hasPermission('complaints-manage-manufacturer'));
        this.hasAccessTo.editStatus = await this.permissionsService.hasPermission('complaints-status') && ((this.data.complaint.assignedTo && this.data.complaint.assignedTo.startsWith(this.getUserType())) || await this.permissionsService.hasPermission('complaints-manage-manufacturer'));
        this.hasAccessTo.editAssign = await this.permissionsService.hasPermission('complaints-assign') && ((this.data.complaint.assignedTo && this.data.complaint.assignedTo.startsWith(this.getUserType())) || await this.permissionsService.hasPermission('complaints-manage-manufacturer'));
        this.hasAccessTo.editComplaintOrderNumber = this.hasAccessTo.manageComplaint && this.configService.user?.user?.ActiveCompanyLicenseType === 'license';
    }

    getAssignToValue() {
        if (this.data.complaint.assignedTo === 'distributor') {
            return 'distributor-' + this.data.complaint.assignedCompanyId;
        }
        return this.data.complaint.assignedTo;
    }

    getUserType() {
        return this.complaintService.getUserType();
    }

    objectLength(obj: Record<string, any>) {
        return Common.isObject(obj) ? Object.keys(obj).length : 0;
    }

    allPositionsHaveConfiguration(): boolean {
        if (this.configService.user?.user?.ActiveCompanyLicenseType === 'license') {
            return true;
        }
        return this.complaintService.allPositionsHaveConfiguration(this.data.positions);
    }

    changePriority(priorityGroup: string, priorityValue: number) {
        const paddedPriorityValue = priorityValue.toString().padStart(2, '0');
        const priority = priorityGroup + paddedPriorityValue;
        this.complaintService.changePriority(this.data.complaint, priority);
    }

    save() {
        const normalizeToDate = (date: Date | null): number | null => {
            if (!date) {
                return null;
            }
            const normalizedDate = new Date(date);
            normalizedDate.setHours(0, 0, 0, 0);
            return normalizedDate.getTime();
        }

        const formDocumentPurchaseDate = this.documentPurchaseDate ? normalizeToDate(parseDate(this.documentPurchaseDate)) : null;
        const documentPurchaseDate = this.data.complaint.purchaseDate ? normalizeToDate(parseDate(this.data.complaint.purchaseDate)) : null;
        if (formDocumentPurchaseDate !== documentPurchaseDate) {
            this.complaintService.changePurchaseDate(this.data.complaint, this.documentPurchaseDate);
        }
        let changedStatus = false;
        const userType = this.getUserType();
        const actualStatusId = this.getActualStatusId(userType);
        if (this.documentStatus !== actualStatusId) {
            this.complaintService.changeStatus(this.data.complaint, this.documentStatus, this.getUserType());
            changedStatus = true;
        }
        if (this.documentName !== this.data.complaint.documentName) {
            this.complaintService.changeDocumentName(this.data.complaint, this.documentName);
        }
        if (this.documentComplaintOrderNumber !== this.data.complaint.complaintOrderNumber) {
            this.complaintService.changeComplaintOrderNumber(this.data.complaint, this.documentComplaintOrderNumber);
        }
        if (this.documentComplaintType !== this.data.complaint.documentTypeId) {
            this.data.complaint.documentTypeId = this.documentComplaintType;
            this.complaintService.changeComplaintType(this.data.complaint, this.documentComplaintType);
            this.complaintType = this.documentComplaintType;
            if (userType !== 'authorizedRetailer') {
                this.complaintService.changeStatus(this.data.complaint, 0, 'authorizedRetailer');
            }
            if (userType !== 'distributor') {
                this.complaintService.changeStatus(this.data.complaint, 0, 'distributor');
            }
            if (userType !== 'manufacturer') {
                this.complaintService.changeStatus(this.data.complaint, 0, 'manufacturer');
            }
            changedStatus = true;
        }
        if (this.documentAssignedTo !== this.getAssignToValue()) {
            this.complaintService.changeAssignedTo(this.data.complaint, this.documentAssignedTo);
        }
        if (this.documentResponsibleUser !== this.data.complaint[this.getUserType()]?.userId) {
            this.complaintService.changeResponsibleUser(this.data.complaint, this.documentResponsibleUser);
        }
        if (this.documentAssignedUser !== this.data.complaint[this.getUserType() + 'UserId' as 'authorizedRetailerUserId' | 'distributorUserId' | 'manufacturerUserId']) {
            this.complaintService.changeAssignedUser(this.data.complaint, this.documentAssignedUser);
        }
        this.changePriority(this.documentPriorityGroup, this.documentPriorityValue);

        if (changedStatus && this.documentStatus) {
            this.complaintService.triggerWorkflow('status_change', this.data.complaint, {
                documentStatus: this.documentStatus,
                level: this.getUserType(),
            }, this.data.positionsWorkflowsGroups);
        }

        this.dialogRef.close();
    }

    getActualStatusId(userType: string) {
        let actualStatusId = this.data.complaint.statusId;
        if (userType === 'authorizedRetailer') {
            actualStatusId = +this.data.complaint.authorizedRetailerStatusId;
        } else if (userType === 'distributor') {
            actualStatusId = +this.data.complaint.distributorStatusId;
        } else {
            actualStatusId = +this.data.complaint.manufacturerStatusId;
        }
        return actualStatusId;
    }

    close() {
        this.dialogRef.close();
    }

    async changedType() {
        const complaint = core.copy(this.data.complaint);
        complaint.documentTypeId = +this.documentComplaintType;
        complaint.authorizedRetailerStatusId = 0;
        complaint.distributorStatusId = 0;
        complaint.manufacturerStatusId = 0;
        const {positionsWorkflowsGroups} = await this.complaintService.getPositionsWorkflows(complaint, this.data.positions);
        this.data.positionsWorkflowsGroups = positionsWorkflowsGroups;
        this.statuses = this.complaintService.getAvailableStatuses(complaint, this.data.positions, this.data.positionsWorkflowsGroups, this.data.appointments, this.getUserType());
        this.status = this.statuses[0];
        this.documentStatus = this.status?.id ?? null;
    }
}
